/**
 * Theme Toggle Component
 *
 * A small toggle switch for dark/light/system theme selection.
 */

import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useSettings, Theme } from '@/contexts/SettingsContext';

interface ThemeToggleProps {
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

export function ThemeToggle({ showLabel = false, size = 'sm' }: ThemeToggleProps) {
  const { settings, setTheme } = useSettings();

  const themes: { value: Theme; icon: React.ReactNode; label: string }[] = [
    { value: 'light', icon: <Sun className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />, label: 'Light' },
    { value: 'dark', icon: <Moon className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />, label: 'Dark' },
    { value: 'system', icon: <Monitor className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />, label: 'System' },
  ];

  return (
    <div className="flex items-center gap-2">
      {showLabel && (
        <span className="text-xs text-muted-foreground">Theme</span>
      )}
      <div className="flex items-center rounded-full p-0.5 bg-secondary border border-border">
        {themes.map((theme) => (
          <button
            key={theme.value}
            onClick={() => setTheme(theme.value)}
            title={theme.label}
            className={`
              ${size === 'sm' ? 'p-1.5' : 'p-2'}
              rounded-full transition-all duration-200
              ${settings.theme === theme.value
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            {theme.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

// Simple icon-only toggle that cycles through themes
export function ThemeToggleSimple() {
  const { settings, toggleTheme, resolvedTheme } = useSettings();

  const getIcon = () => {
    if (settings.theme === 'system') {
      return <Monitor className="w-4 h-4" />;
    }
    return resolvedTheme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />;
  };

  const getLabel = () => {
    if (settings.theme === 'system') return 'System theme';
    return settings.theme === 'dark' ? 'Dark theme' : 'Light theme';
  };

  return (
    <button
      onClick={toggleTheme}
      title={getLabel()}
      className="p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
    >
      {getIcon()}
    </button>
  );
}

export default ThemeToggle;
